import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from "react";
import { getLeagueInfo } from "../../Controllers/LeagueSyncApi";

interface LeagueInfoContextType {
  leagueData: any[];
  filteredLeagueData: any[];
  isLoading: boolean;
  error: string | null;
  refetchLeagueInfo: () => Promise<void>;
}

interface LeagueInfoProviderProps {
  children: ReactNode;
}

// Create Context
const LeagueInfoContext = createContext<LeagueInfoContextType | undefined>(undefined);

// Provider Component
export const LeagueInfoProvider: React.FC<LeagueInfoProviderProps> = ({ children }) => {
  const [leagueData, setLeagueData] = useState<any[]>([]);
  const [filteredLeagueData, setFilteredLeagueData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Sort leagues by last_updated in descending order (most recent first)
  const sortLeaguesByLastUpdated = (leagues: any[]): any[] => {
    return leagues.sort((a: any, b: any) => {
      const dateA = new Date(a.last_updated || 0);
      const dateB = new Date(b.last_updated || 0);
      return dateB.getTime() - dateA.getTime();
    });
  };

  const fetchLeagueInfo = async () => {
    // Prevent duplicate API calls during initialization
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getLeagueInfo();
      console.log("League Info Response:", response);

      if (response.status === 200 && response.data) {
        // Sort leagues by last_updated in descending order (most recent first)
        const sortedLeagues = sortLeaguesByLastUpdated(response.data);
        
        setLeagueData(sortedLeagues);
        
        // Filter leagues with valid league_name and maintain the sorted order
        const filtered = sortedLeagues.filter(
          (leagueCard: any) => leagueCard?.league_name,
        );
        setFilteredLeagueData(filtered);
      } else {
        setLeagueData([]);
        setFilteredLeagueData([]);
        setError("Failed to fetch league information");
        console.error("Failed to fetch league information");
      }
    } catch (error) {
      setLeagueData([]);
      setFilteredLeagueData([]);
      setError("Error fetching league info");
      console.error("Error fetching league info:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const refetchLeagueInfo = async () => {
    await fetchLeagueInfo();
  };


  const contextValue: LeagueInfoContextType = {
    leagueData,
    filteredLeagueData,
    isLoading,
    error,
    refetchLeagueInfo,
  };

  return (
    <LeagueInfoContext.Provider value={contextValue}>
      {children}
    </LeagueInfoContext.Provider>
  );
};

// Custom Hook
export const useLeagueInfo = (): LeagueInfoContextType => {
  const context = useContext(LeagueInfoContext);
  
  if (context === undefined) {
    throw new Error("useLeagueInfo must be used within a LeagueInfoProvider");
  }
  
  return context;
};

export default LeagueInfoContext;
